/**
 * The documented option tree, marked with one valve's effective configuration. Nothing is computed here that the
 * configurator does not already say: a part node is «valgt» when its part number is in the proposal's BOM, a group is
 * «må avklares» when the BOM line it stands for has no part number yet, the adapter follows the flange check.
 */
export const STATUS_LABEL = { valgt: "Valgt", valgbar: "Kan velges", ikke_valgbar: "Dokumentert · ikke valgbar ennå", auto: "Følger automatisk", avklar: "Må avklares", na: "Ikke aktuelt", gruppe: "", rot: "" };

const matches = (want, have) => (Array.isArray(want) ? want.includes(have) : want === have);

/** Which requirement a node has that the configuration does not meet right now – {field, want, have} or null. */
export function conflictOf(node, fields, p) {
  if (!node.requires) return null;
  for (const [field, want] of Object.entries(node.requires)) {
    const have = fields[field] ?? p?.[field] ?? null;
    if (!matches(want, have)) return { field, want: Array.isArray(want) ? want[0] : want, have };
  }
  return null;
}

/** True when the draft already asks for exactly what this node selects (the preview may still be on its way). */
export const inDraft = (node, fields) => Boolean(node.select) && Object.entries(node.select).every(([k, val]) => fields[k] === val);

export function markTree(doc, v, p, fields, hasValve = true) {
  const parts = new Set((p?.bom || []).map((l) => l.part).filter(Boolean));
  const byKey = Object.fromEntries((p?.bom || []).map((l) => [l.key, l]));
  const opts = p?.options || {};
  const flange = p?.flange || {};
  const remoteKnown = !hasValve || fields.remote != null || v.feedback != null;
  const indicatorKnown = !hasValve || fields.indicator != null || v.mech_indicator != null;
  const index = {};
  const walk = (n, depth, parent) => {
    const children = (n.children || []).map((c) => walk(c, depth + 1, n));
    const selected = hasValve && (n.part ? parts.has(n.part)
      : n.id === "no_indicator" ? opts.indicator === false && indicatorKnown
        : n.id === "no_remote" ? opts.remote == null && remoteKnown
          : n.id === "no_manual" ? (opts.manual || "none") === "none"
            : children.some((c) => c.selected));
    let open = null;
    let na = false;
    if (!hasValve) { /* the general map: nothing is open, nothing is chosen */ }
    else if (n.bom_key === "adapter") { if (flange.needed === false) na = true; else if (flange.ok !== true) open = flange.detail || "Ventilens flens er ikke oppgitt"; }
    else if (n.bom_key) { const ln = byKey[n.bom_key]; if (ln && !ln.part && ln.status !== "dokumentert") open = ln.name; }
    if (hasValve && n.id === "direct" && !remoteKnown) open = open || "Fjernindikering er ikke oppgitt i ventillisten – velg bryter, kontinuerlig eller ingen";
    if (hasValve && n.id === "indirect" && !indicatorKnown) open = open || "Lokal indikator er ikke oppgitt i ventillisten – bekreft med eller uten";
    const status = n.rule === "rot" ? "rot" : na ? "na" : selected ? "valgt" : open ? "avklar" : n.rule === "auto" ? "auto" : n.rule === "ikke_valgbar" ? "ikke_valgbar" : n.rule === "valgbar" ? "valgbar" : "gruppe";
    const out = { ...n, children, depth, parentId: parent?.id || null, selected, open, status, pending: hasValve && !selected && inDraft(n, fields), canChoose: hasValve && n.rule === "valgbar" && !selected, chosen: null, anyOpen: Boolean(open) || children.some((c) => c.anyOpen) };
    if (children.length && selected) { let c = children.find((x) => x.selected); while (c && c.children.length) c = c.children.find((x) => x.selected) || c; out.chosen = c && !c.children.length ? c : c || null; }
    index[n.id] = out;
    return out;
  };
  const root = walk(doc, 0, null);
  const all = Object.values(index);
  return { root, index, openCount: all.filter((x) => x.status === "avklar" && !x.children.some((c) => c.status === "avklar")).length, selectedParts: all.filter((x) => x.part && x.selected).length };
}

/** The first view: with a valve – root, branches and the groups folded, each saying what is chosen or open on it; without – the structure two levels deep. */
export function defaultExpanded(marked, hasValve = true) {
  const set = new Set();
  const walk = (n) => { if (n.depth <= (hasValve ? 1 : 2)) set.add(n.id); n.children.forEach(walk); };
  walk(marked.root);
  return set;
}

/** Visible nodes in reading order (a node shows when every ancestor is expanded). */
export function visibleNodes(marked, expanded) {
  const out = [];
  const walk = (n) => { out.push(n); if (expanded.has(n.id)) n.children.forEach(walk); };
  walk(marked.root);
  return out;
}

/** «Indikering › Direkte indikering» – where a node sits, for the panel's eyebrow. */
export function pathOf(marked, id) {
  const names = [];
  let n = marked.index[id]?.parentId ? marked.index[marked.index[id].parentId] : null;
  while (n && n.depth > 0) { names.unshift(n.label); n = n.parentId ? marked.index[n.parentId] : null; }
  return names.join(" › ");
}

/** All ids whose label, part number, cell or extra parts contain the query – plus every ancestor, so they can be shown. */
export function searchTree(marked, q) {
  const s = q.trim().toLowerCase();
  if (!s) return { hits: null, ancestors: new Set() };
  const hits = new Set();
  const ancestors = new Set();
  Object.values(marked.index).forEach((n) => {
    const hay = [n.label, n.part, n.cell, n.sub, ...(n.extra || []).flatMap((x) => [x.part, x.label])].filter(Boolean).join(" ").toLowerCase();
    if (hay.includes(s)) { hits.add(n.id); let p = n.parentId; while (p) { ancestors.add(p); p = marked.index[p].parentId; } }
  });
  return { hits, ancestors };
}

export const allIds = (marked) => new Set(Object.keys(marked.index));

export const FIELD_LABEL = { material: "Material", connection: "Tilkobling", operating: "Drift", indicator: "Lokal indikator", remote: "Fjernindikering", switch: "Brytertype", manual: "Nødbetjening", stem: "Spindel / hylse" };
export const OPERATING_LABEL = { N: "Normal", N2: "Normal, revidert hylse/gaffel", S: "Nedsenket", S2: "Nedsenket, revidert hylse/gaffel" };
export const MATERIAL_LABEL = { A: "A – standard, støpejern", B: "B – standard, stålsylinder", C: "C – høy temperatur (PTFE/Viton)", D: "D – lav temperatur" };
export const valueLabel = (field, val) => (field === "operating" ? OPERATING_LABEL[val] || val : field === "material" ? MATERIAL_LABEL[val] || val : String(val));
